import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const UploadStatusContext = createContext();

export const useUploadStatus = () => {
  const context = useContext(UploadStatusContext);
  if (!context) {
    throw new Error('useUploadStatus must be used within UploadStatusProvider');
  }
  return context;
};

// Generic background task: { id, label, progress?, estimatedTimeRemaining?, cancel?: () => void }
export const UploadStatusProvider = ({ children }) => {
  const [saveRun, setSaveRun] = useState({ active: false, label: 'Saving run...' });

  const [cloudSyncRun, setCloudSyncRun] = useState({ active: false, label: 'Syncing to cloud...' });

  const [youtubeUpload, setYoutubeUpload] = useState({
    active: false,
    progress: 0,
    runId: null,
    fileName: null,
    startTime: null,
    estimatedTimeRemaining: null,
    abortController: null,
  });

  const [goproExtraction, setGoproExtraction] = useState({
    active: false,
    progress: 0,
    runId: null,
    startTime: null,
    estimatedTimeRemaining: null,
    abortController: null,
  });

  const [genericTasks, setGenericTasks] = useState([]);

  const [exportVideo, setExportVideo] = useState({
    active: false,
    phase: null, // 'loading' | 'recording' | 'uploading' | 'processing' | 'complete' | 'failed'
    jobId: null,
    downloadURL: null,
    error: null,
    progress: null, // 0–100 when recording/uploading
    statusMessage: null, // server export: "Downloading video…", "Rendering overlay…", etc.
    currentTime: null, // seconds
    duration: null, // seconds
    frameIndex: null,
    estimatedTimeRemaining: null, // seconds
  });

  const startExportVideo = useCallback((opts = {}) => {
    setExportVideo({
      active: true,
      phase: opts.phase || 'loading',
      jobId: opts.jobId || null,
      downloadURL: null,
      error: null,
      progress: null,
      statusMessage: null,
      currentTime: null,
      duration: null,
      frameIndex: null,
      estimatedTimeRemaining: null,
    });
  }, []);

  const updateExportVideo = useCallback((updates) => {
    setExportVideo((prev) => (prev.active ? { ...prev, ...updates } : prev));
  }, []);

  const clearExportVideo = useCallback(() => {
    setExportVideo({
      active: false,
      phase: null,
      jobId: null,
      downloadURL: null,
      error: null,
      progress: null,
      statusMessage: null,
      currentTime: null,
      duration: null,
      frameIndex: null,
      estimatedTimeRemaining: null,
    });
  }, []);

  // Listen for server export job completion and progress (persists when user navigates away)
  useEffect(() => {
    const jobId = exportVideo.jobId;
    const phase = exportVideo.phase;
    if (!jobId || phase !== 'processing') return;
    const unsubscribe = onSnapshot(doc(db, 'exportJobs', jobId), (snap) => {
      const data = snap.data();
      if (data?.status === 'ready' && data?.downloadURL) {
        setExportVideo((prev) => (prev.jobId === jobId ? { ...prev, phase: 'complete', downloadURL: data.downloadURL } : prev));
      } else if (data?.status === 'failed' && data?.error) {
        setExportVideo((prev) => (prev.jobId === jobId ? { ...prev, phase: 'failed', error: data.error } : prev));
      } else if (data?.status === 'processing') {
        setExportVideo((prev) => (prev.jobId === jobId ? {
          ...prev,
          statusMessage: data?.statusMessage || null,
          progress: typeof data?.progress === 'number' ? data.progress : prev.progress,
        } : prev));
      }
    });
    return () => unsubscribe();
  }, [exportVideo.jobId, exportVideo.phase]);

  const startSaveRun = useCallback((label = 'Saving run...') => {
    setSaveRun({ active: true, label });
  }, []);

  const completeSaveRun = useCallback(() => {
    setSaveRun({ active: false, label: 'Saving run...' });
  }, []);

  const startCloudSyncRun = useCallback((label = 'Syncing to cloud...') => {
    setCloudSyncRun({ active: true, label });
  }, []);

  const completeCloudSyncRun = useCallback(() => {
    setCloudSyncRun({ active: false, label: 'Syncing to cloud...' });
  }, []);

  const addGenericTask = useCallback((task) => {
    const id = task.id || `task-${Date.now()}`;
    setGenericTasks((prev) => [...prev, { ...task, id }]);
    return id;
  }, []);

  const updateGenericTask = useCallback((id, updates) => {
    setGenericTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
  }, []);

  const removeGenericTask = useCallback((id) => {
    setGenericTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const startYoutubeUpload = (runId, fileName, abortController = null) => {
    setYoutubeUpload({
      active: true,
      progress: 0,
      runId,
      fileName,
      startTime: Date.now(),
      estimatedTimeRemaining: null,
      abortController,
    });
  };

  const updateYoutubeUpload = (progress, estimatedTimeRemaining = null) => {
    setYoutubeUpload(prev => ({
      ...prev,
      progress,
      estimatedTimeRemaining,
    }));
  };

  const completeYoutubeUpload = () => {
    setYoutubeUpload({
      active: false,
      progress: 0,
      runId: null,
      fileName: null,
      startTime: null,
      estimatedTimeRemaining: null,
      abortController: null,
    });
  };

  const cancelYoutubeUpload = () => {
    const { abortController, runId } = youtubeUpload;
    if (abortController) {
      abortController.abort();
    }
    setYoutubeUpload(prev => ({
      ...prev,
      active: false,
      progress: 0,
      abortController: null,
    }));
    return runId; // Return runId so component can update Firestore
  };

  const startGoproExtraction = (runId, abortController = null) => {
    setGoproExtraction({
      active: true,
      progress: 0,
      runId,
      startTime: Date.now(),
      estimatedTimeRemaining: null,
      abortController,
    });
  };

  const updateGoproExtraction = (progress, estimatedTimeRemaining = null) => {
    setGoproExtraction(prev => ({
      ...prev,
      progress,
      estimatedTimeRemaining,
    }));
  };

  const completeGoproExtraction = () => {
    setGoproExtraction({
      active: false,
      progress: 0,
      runId: null,
      startTime: null,
      estimatedTimeRemaining: null,
      abortController: null,
    });
  };

  const cancelGoproExtraction = () => {
    const { abortController } = goproExtraction;
    if (abortController) {
      abortController.abort();
    }
    setGoproExtraction(prev => ({
      ...prev,
      active: false,
      progress: 0,
      abortController: null,
    }));
  };

  return (
    <UploadStatusContext.Provider
      value={{
        saveRun,
        startSaveRun,
        completeSaveRun,
        cloudSyncRun,
        startCloudSyncRun,
        completeCloudSyncRun,
        youtubeUpload,
        goproExtraction,
        startYoutubeUpload,
        updateYoutubeUpload,
        completeYoutubeUpload,
        cancelYoutubeUpload,
        startGoproExtraction,
        updateGoproExtraction,
        completeGoproExtraction,
        cancelGoproExtraction,
        genericTasks,
        addGenericTask,
        updateGenericTask,
        removeGenericTask,
        exportVideo,
        startExportVideo,
        updateExportVideo,
        clearExportVideo,
      }}
    >
      {children}
    </UploadStatusContext.Provider>
  );
};

